import { Icon } from "@iconify/react";
import React, { useEffect, useRef, useState } from "react";
import { useEscapeToClose } from "../../hooks/use-esc-to-close";
import { useId } from "../../hooks/use-id";

/**
 * Renderiza el contenedor raíz del acordeón.
 *
 * Este componente envuelve los items del acordeón dentro de una lista no ordenada (`ul`)
 * y se encarga únicamente de la estructura externa del conjunto.
 *
 * Uso esperado:
 * - Debe contener uno o varios `Accordion.Item` como hijos directos.
 * - Puede recibir clases CSS adicionales para personalizar su apariencia.
 *
 * @param {Object} props Props del componente.
 * @param {React.ReactNode} props.children Hijos que serán renderizados dentro del acordeón.
 * Normalmente deben ser instancias de `Accordion.Item`.
 * @param {string} [props.className] Clases CSS adicionales que se añadirán al elemento `ul`.
 * @returns {JSX.Element} Contenedor del acordeón.
 */
const Container = ({ children, className }) => {
	return <ul className={`accordion ${className ? className : ""}`}>{children}</ul>;
};

/**
 * Renderiza un item del acordeón.
 *
 * Este componente controla internamente el estado expandido/colapsado del item y además:
 * - Genera IDs únicos para enlazar accesiblemente el botón con su contenido.
 * - Clona automáticamente `Accordion.ItemButton` para inyectarle:
 *   - `isExpanded`
 *   - `id`
 *   - `ariaControls`
 *   - una función `onClick` que alterna el estado del item
 * - Clona automáticamente `Accordion.Content` para inyectarle:
 *   - `id`
 *   - `arialLabeledby`
 *   - `isExpanded`
 * - Cierra el item cuando se presiona la tecla Escape.
 * - Cierra el item cuando se hace clic por fuera del mismo.
 *
 * Uso esperado:
 * - Debe ser hijo directo de `Accordion.Container`.
 * - Normalmente debe contener un `Accordion.ItemButton` y un `Accordion.Content`.
 *
 * Nota:
 * Aunque recibe la prop `expanded`, el estado visual del item se maneja internamente
 * mediante `isExpanded`, sincronizándose con dicha prop cuando cambia.
 *
 * @param {Object} props Props del componente.
 * @param {React.ReactNode} props.children Contenido del item. Se espera principalmente
 * `Accordion.ItemButton` y `Accordion.Content`, aunque puede contener otros nodos.
 * @param {string} [props.className] Clases CSS adicionales que se añadirán al elemento `li`.
 * @param {boolean} [props.expanded] Indica si el item debe iniciar o sincronizarse como expandido.
 * Si no se proporciona, el item permanecerá colapsado por defecto.
 * @returns {JSX.Element} Item del acordeón.
 */
const Item = ({ children, className, expanded }) => {
	const [isExpanded, setExpanded] = useState(false);
	const baseId = useId("accordion-item");
	const buttonId = `${baseId}-button`;
	const contentId = `${baseId}-content`;


	const ref = useRef(null);
	useEscapeToClose({ enabled: isExpanded, onClose: () => setExpanded(false) });

	useEffect(() => {
		setExpanded(expanded ? true : false);
	}, [expanded]);

	useEffect(() => {
		let timer = null;
		const handleClickOutside = (event) => {
			if (ref.current && !ref.current.contains(event.target)) {
				timer = setTimeout(() => {
					setExpanded(false);
				}, 70);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			clearTimeout(timer);
		};
	}, [ref]);

	return (
		<li
			ref={ref}
			className={`item ${isExpanded ? "expanded" : "collapsed"} ${className ? className : ""}`}
		>
			{React.Children.map(children, (child, index) => {
				if (child?.type === ItemButton) {
					return React.cloneElement(child, {
						...child.props,
						isExpanded: isExpanded,
						id: buttonId,
						ariaControls: contentId,
						onClick: () => {
							child.props.onClick && child.props.onClick();
							setExpanded(!isExpanded);
						},
					});
				} else if (child?.type === Content) {
					return React.cloneElement(child, {
						...child.props,
						id: contentId,
						arialLabelledby: buttonId,
						isExpanded: isExpanded,
					});
				} else {
					return child;
				}
			})}
		</li>
	);
};

/**
 * Renderiza el botón que expande o contrae un `Accordion.Item`.
 *
 * Este componente debe usarse dentro de `Accordion.Item`.
 * El propio `Accordion.Item` se encarga de inyectarle automáticamente:
 * - `id`
 * - `ariaControls`
 * - `isExpanded`
 * - una función `onClick` que alterna el estado expandido
 *
 * Si se proporcionan `children`, estos reemplazan completamente el contenido visual
 * por defecto del botón. Si no se proporcionan, se mostrará:
 * - un texto basado en `label`
 * - un ícono de flecha decorativo
 *
 * Uso esperado:
 * - Debe ser hijo de `Accordion.Item`.
 * - No es necesario pasar manualmente `id`, `ariaControls` ni `isExpanded` al usarlo
 * dentro del acordeón, porque esos valores los gestiona `Accordion.Item`.
 *
 * @param {Object} props Props del componente.
 * @param {React.ReactNode} [props.children] Contenido personalizado del botón.
 * Si se pasa, reemplaza el contenido por defecto.
 * @param {string} [props.className] Clases CSS adicionales que se añadirán al botón.
 * @param {string} [props.label] Texto a mostrar cuando no se proporciona `children`.
 * Si no se pasa, se usa `"N/A"` como valor por defecto.
 * @param {Function} [props.onClick] Función que se ejecuta al hacer clic en el botón.
 * Esta función es complementaria a la lógica interna de expansión/colapso.
 * @param {boolean} [props.isExpanded] Estado expandido actual del item.
 * Normalmente lo inyecta automáticamente `Accordion.Item`.
 * @param {string} [props.id] ID del botón para enlazarlo accesiblemente con el contenido.
 * Normalmente lo inyecta automáticamente `Accordion.Item`.
 * @param {string} [props.ariaControls] ID del panel controlado por el botón.
 * Normalmente lo inyecta automáticamente `Accordion.Item`.
 * @returns {JSX.Element} Botón del item del acordeón.
 */
const ItemButton = ({ children, className, label, onClick, isExpanded, id, ariaControls }) => {
	return (
		<button
			id={id}
            type="button"
			className={`itemButton focusable-secondary ${className ? className : ""}`}
			aria-expanded={isExpanded}
			aria-controls={ariaControls}
			onClick={() => onClick && onClick()}
		>
			{children ? (
				children
			) : (
				<>
					<span> {label || "N/A"}</span>
					<Icon
						aria-hidden
						className="icon"
						icon="material-symbols:keyboard-arrow-down"
					/>
				</>
			)}
		</button>
	);
};

/**
 * Renderiza el contenido expandible de un `Accordion.Item`.
 *
 * Este componente debe usarse dentro de `Accordion.Item`.
 * El propio `Accordion.Item` se encarga de inyectarle automáticamente:
 * - `id`
 * - `arialLabeledby`
 * - `isExpanded`
 *
 * Comportamiento actual:
 * - Si `isExpanded` es `false`, el contenido no se renderiza.
 * - Si `isExpanded` es `true`, el contenido se renderiza dentro de un `div`
 *   con rol de región (`role="region"`).
 *
 * Uso esperado:
 * - Debe ser hijo de `Accordion.Item`.
 * - No es necesario pasar manualmente `id` ni `isExpanded`, ya que los gestiona
 * `Accordion.Item`.
 *
 * @param {Object} props Props del componente.
 * @param {React.ReactNode} props.children Contenido interno del panel expandible.
 * Puede contener cualquier tipo de nodo o componente React.
 * @param {string} [props.className] Clases CSS adicionales que se añadirán al contenedor del contenido.
 * @param {string} [props.arialLabelledby] ID del botón que etiqueta accesiblemente la región.
 * Normalmente lo inyecta automáticamente `Accordion.Item`.
 * @param {string} [props.id] ID del contenedor del contenido.
 * Normalmente lo inyecta automáticamente `Accordion.Item`.
 * @param {boolean} [props.isExpanded] Indica si el contenido debe renderizarse.
 * Normalmente lo inyecta automáticamente `Accordion.Item`.
 * @returns {JSX.Element|null} Contenido del acordeón o `null` si el item está colapsado.
 */
const Content = ({ children, className, arialLabelledby, id, isExpanded }) => {
	return (
		<div
			id={id}
            role="region"
			className={`content ${className ? className : ""}`}
			aria-hidden={!isExpanded}
			aria-labelledby={arialLabelledby}
            hidden={!isExpanded}
		>
			{isExpanded? children : null}
		</div>
	);
};

/**
 * Objeto agrupador que expone todos los subcomponentes necesarios para construir
 * un acordeón compuesto.
 *
 * Subcomponentes disponibles:
 * - `Accordion.Container`: contenedor principal del acordeón.
 * - `Accordion.Item`: item individual con control de expansión.
 * - `Accordion.ItemButton`: botón que alterna la expansión del item.
 * - `Accordion.Content`: panel expandible asociado al item.
 *
 * Ejemplo de uso:
 * ```jsx
 * <Accordion.Container>
 *   <Accordion.Item>
 *     <Accordion.ItemButton label="Mi sección" />
 *     <Accordion.Content>
 *       Contenido del acordeón
 *     </Accordion.Content>
 *   </Accordion.Item>
 * </Accordion.Container>
 * ```
 */
export const Accordion = {
	Container,
	Item,
	Content,
	ItemButton,
};
